// Components
import ScrollAnimation from './scroll-animation';

const settings = {
    classNames: {
        glint: 'video-play-button__glint',
        icon: 'video-play-button__icon',
        iconVisible: 'video-play-button__icon--visible',
    },

    selectors: {
        containers: '.video-play-button',
        associatedButtons: '*[data-video-play-button-id]',
    },

    animation: {
        defaultScrollAnimationParameters: {
            endCap: 1,
            easing: 'none',
        },
        scrollTriggerPosition: 0.2,
    },
};

export default class VideoPlayButton extends ScrollAnimation {
    constructor(container) {
        super(
            container,
            settings.defaultScrollAnimationParameters,
        );

        /** @type {boolean} */
        this.visible = false;

        /** @type {Element} */
        this.container = container;

        // Add icon & glint elements
        /** @type {Element} */
        this.icon = document.createElement('div');
        this.icon.className = settings.classNames.icon;
        this.container.appendChild(this.icon);

        /** @type {Element} */
        this.glint = document.createElement('div');
        this.glint.className = settings.classNames.glint;
        this.container.appendChild(this.glint);

        // Set our min delay flag - we're doing this to add
        // an initial delay after page load before this animation
        // becomes available, so that it doesn't play if users
        // arrive at the page (e.g., via a #hash) scrolled to a
        // video
        /** @type {boolean} */
        this.showImmediately = true;
        setTimeout(
            () => {
                this.showImmediately = false;
            },
            500,
        );
    }

    static init() {
        // Find elements with the expected class name
        let elements = document
            .querySelectorAll(settings.selectors.containers);

        for (let i=0; i<elements.length; i++) {
            new VideoPlayButton(elements[i]);
        }

        // Find other elements (buttons) that may also
        // present videos via an association with a VideoPlayButton
        elements = document
            .querySelectorAll(settings.selectors.associatedButtons);

        for (let i=0; i<elements.length; i++) {
            const element = elements[i];

            // Try to find the VideoPlayButton associated with this
            // element
            const associatedId = element.getAttribute('data-video-play-button-id');
            const associatedElement = document.querySelector(
                settings.selectors.containers +
                `#${associatedId}`,
            );

            if (!associatedElement) {
                continue;
            }

            // A VideoPlayButton was found - set up a click event
            element.addEventListener('keydown', (event) => {
                if (event.key != 'Enter') {
                    return;
                }

                event.preventDefault();
                event = new KeyboardEvent(
                    'keydown',
                    {
                        key: 'Enter',
                    },
                );

                associatedElement.dispatchEvent(event);
            });

            element.addEventListener('click', (event) => {
                event.preventDefault();

                // Click the associated button
                associatedElement.click();
            });
        }
    }

    // From ScrollAnimation
    setAnimationProgress(
        overallProgress,
        topProgress,
        centerProgress,
    ) {
        // Call base class
        super.setAnimationProgress(
            overallProgress,
            topProgress,
            centerProgress,
        );

        if (this.visible) {
            if (
                centerProgress <
                settings.animation.scrollTriggerPosition
            ) {
                // Hide the icon
                this.hide();
            }
        } else {
            if (
                centerProgress >=
                settings.animation.scrollTriggerPosition
            ) {
                // Show the icon - checking if enough time has passed since page load
                if (this.showImmediately) {
                    this.showImmediately = false;
                    this.show(true);
                } else {
                    this.show();
                }
            }
        }
    }

    show(immediate) {
        if (this.visible) {
            return;
        }

        this.visible = true;

        // Stop any ongoing animation
        if (this.showHideTimeline) {
            this.showHideTimeline.progress(1);
        }

        if (immediate) {
            // Add our "visible" class
            this.container
                .classList
                .add(settings.classNames.iconVisible);

            // Stop here
            return;
        }

        this.showHideTimeline = gsap.timeline();

        // Show glint & icon
        this.showHideTimeline.to(
            [
                this.glint,
                this.icon,
            ],
            0.5,
            {
                ease: 'power1.inOut',
                opacity: 1,
            },
        );

        // Add our "visible" class
        this.showHideTimeline.add(() => {
            this.container
                .classList
                .add(settings.classNames.iconVisible);
        });

        // Hide glint
        this.showHideTimeline.to(
            this.glint,
            0.5,
            {
                ease: 'power1.inOut',
                opacity: 0,
            },
        );

        // Scale up the icon
        this.showHideTimeline.from(
            [
                this.glint,
                this.icon,
            ],
            this.showHideTimeline.duration(),
            {
                ease: 'power3.out',
                scale: 0.75,
            },
            0,
        );

        this.showHideTimeline.add(() => {
            this.showHideTimeline = null;

            // Clean up
            this.glint.removeAttribute('style');
            this.icon.removeAttribute('style');
        });
    }

    hide() {
        if (!this.visible) {
            return;
        }

        this.visible = false;

        // Stop any ongoing animation
        if (this.showHideTimeline) {
            this.showHideTimeline.progress(1);
        }

        // Add our "visible" class (should already be present)
        this.container
            .classList
            .add(settings.classNames.iconVisible);

        this.showHideTimeline = gsap.timeline();

        // Hide icon
        this.showHideTimeline.to(
            this.icon,
            0.5,
            {
                ease: 'power2.out',
                opacity: 0,
            },
        );

        this.showHideTimeline.add(() => {
            this.showHideTimeline = null;

            // Remove our "visible" class
            this.container
                .classList
                .remove(settings.classNames.iconVisible);

            // Clean up
            this.icon.removeAttribute('style');
        });
    }
}
